package fyi.moko.hotpl.domain.model

/**
 * A small, curated set of place kinds derived from the place's existing [tags] — no new
 * field, no user input, nothing persisted. Originally added to pick a grayscale fallback
 * illustration when a place has no user photo; it now also backs the Journal's category
 * filter, which is why [label] exists.
 *
 * 질의 축으로 쓰기 전에 저장 컬럼으로 승격하지 않은 이유는 §1.8 판정 절차 때문이다 —
 * 태그 추론만으로 필터를 먼저 굴려 보고, 실제로 반복해서 쓰이는 게 확인되면 그때
 * 마이그레이션을 가진 진짜 필드로 옮긴다. 그래서 추론 실패(=null)는 버그가 아니라
 * "아직 분류할 재료가 없다"는 정상 상태다.
 *
 * Declaration order is the match priority: [fromTags] returns the first category
 * that any of the place's tags maps to (so a coffee-forward "cafe" wins over a
 * "dessert" that would otherwise read as [BAKERY]). Unmapped tags leave the
 * category null, and the UI falls back to a generic placeholder.
 */
enum class PlaceCategory(val label: String, internal val tags: Set<String>) {
    CAFE("Cafe", setOf("cafe", "coffee", "espresso", "latte", "americano", "tea", "roastery", "cafeteria")),
    BAKERY("Bakery", setOf("bakery", "dessert", "bread", "pastry", "patisserie", "cake", "donut")),
    BAR(
        "Bar",
        setOf(
            "bar", "pub", "wine", "winery", "brewery", "beer", "cocktail", "whisky",
            "izakaya", "nightclub", "soju",
        ),
    ),
    RESTAURANT(
        "Restaurant",
        setOf(
            "restaurant", "korean", "bbq", "noodles", "dumplings", "tofu", "dinner",
            "lunch", "galbijjim", "banchan", "soup", "spicy", "ramen", "sushi",
            "pizza", "burger", "diner",
        ),
    ),
    BEACH("Beach", setOf("beach", "ocean", "sea", "coast", "seaside", "shore")),
    LAKE("Lake", setOf("lake", "pond", "river", "riverside", "reservoir")),
    MOUNTAIN("Mountain", setOf("mountain", "hike", "hiking", "trail", "trailhead", "peak", "summit", "valley")),
    PARK("Park", setOf("park", "garden", "botanical", "arboretum")),
    ARCHITECTURE(
        "Architecture",
        setOf(
            "architecture", "building", "museum", "gallery", "landmark", "temple",
            "church", "cathedral", "palace", "tower", "monument",
        ),
    ),
    ;

    companion object {
        /** First category (by declaration priority) whose tag set any of [tags] hits, or null. */
        fun fromTags(tags: List<String>): PlaceCategory? {
            val words = mutableSetOf<String>()
            tags.forEach { raw ->
                // normalizeForComparison collapses whitespace runs, so splitting on a single
                // space here is enough — same normalization the search and tag filters use.
                val normalized = normalizeForComparison(raw)
                if (normalized.isEmpty()) return@forEach
                // 태그 전체와 그 안의 낱말을 모두 후보로 넣는다 — 사용자가 실제로 쓰는 태그는
                // "latte art", "single origin"처럼 여러 낱말인 경우가 많아서, 전체 문자열만
                // 비교하면 카페 태그가 붙은 장소가 통째로 미분류로 남는다.
                words += normalized
                words += normalized.split(" ").filter { it.isNotEmpty() }
            }
            return entries.firstOrNull { category -> category.tags.any { it in words } }
        }
    }
}
